precision highp float;

float map(float value, float min1, float max1, float min2, float max2) {
    return min2 + (value - min1) * (max2 - min2) / (max1 - min1);
}

float sign2(vec2 p1, vec2 p2, vec2 p3) {
    return (p1.x - p3.x) * (p2.y - p3.y) - (p2.x - p3.x) * (p1.y - p3.y);
}

float pointInTriangle(vec2 pt, vec2 v1, vec2 v2, vec2 v3) {
    float d1, d2, d3;
    bool has_neg, has_pos;

    d1 = sign2(pt, v1, v2);
    d2 = sign2(pt, v2, v3);
    d3 = sign2(pt, v3, v1);

    has_neg = (d1 < 0.0) || (d2 < 0.0) || (d3 < 0.0);
    has_pos = (d1 > 0.0) || (d2 > 0.0) || (d3 > 0.0);

    if(!(has_neg && has_pos)) {
        return d1 + d2 + d3;
    } else {
        return 0.0;
    }

}

float random(in vec2 st);
float snoise(in vec3 st);
float noise4d(in vec4 st);

uniform float uTime;
uniform float uSpeed;
uniform float uDistance;
uniform vec3 uIntersectPosition;

varying vec2 vUv; // [0, 1]
varying vec3 vPosition;

const float PI = 3.14159;

const vec4 POND_BLUE = vec4(0.03529411764705882, 0.20392156862745098, 0.19607843137254902, 1.0);

const float HEAD_RADIUS = 0.15;
const float TAIL_LENGTH = 0.65;

const vec2 HEAD_CENTER = vec2(0.5, 1.0 - HEAD_RADIUS);

const float TAIL_H = TAIL_LENGTH;
const float TAIL_X = HEAD_RADIUS;
const float TAIL_Y = sqrt(pow(TAIL_H, 2.0) - pow(TAIL_X, 2.0));
const float TAIL_A = (pow(TAIL_X, 2.0) - pow(TAIL_Y, 2.0) + pow(TAIL_H, 2.0)) / (2.0 * TAIL_H);
const float TAIL_B = sqrt(pow(TAIL_X, 2.0) - pow(TAIL_A, 2.0));

const vec2 TAIL_1 = vec2(0.5 - TAIL_B, TAIL_H - TAIL_A);
const vec2 TAIL_2 = vec2(0.5, 0.0);
const vec2 TAIL_3 = vec2(0.5 + TAIL_B, TAIL_H - TAIL_A);

const float LIGHT_UNDERWATER_FACTOR = 1.3;
const float LIGHT_RADIUS = 0.5 * LIGHT_UNDERWATER_FACTOR;
const vec4 SOFT_RED = vec4(.1, .02, 0.0, 1.0);
const vec4 LIGHT_COLOR = SOFT_RED;

uniform float uIsGlowing;

const vec4 GOLD = vec4(1.0, 0.6, 0.2, 1.0);
const vec4 RED = vec4(1.0, 0.0, 0.0, 1.0);
const vec4 RED_JAPAN = vec4(0.737, 0., 0.176, 1.0);
const vec4 BLACK = vec4(0.0, 0.0, 0.0, 1.0);
const vec4 WHITE = vec4(0.97, 0.97, 0.97, 1.0);
const vec4 EDGE_COLOR = GOLD;

const vec4 KOI_BLACK = vec4(0.02, 0.024, 0.024, 1.0); // 050606
const vec4 KOI_RED = vec4(0.906, 0.059, 0., 1.0); // E70F00
const vec4 KOI_ORANGE = vec4(0.843, 0.365, 0., 1.0); // D75D00
const vec4 KOI_YELLOW = vec4(0.902, 0.569, 0., 1.0); // E69100
const vec4 KOI_WHITE = vec4(0.784, 0.757, 0.729, 1.0); // C8C1BA

uniform float uIndex;

void main() {
    vec2 varyingUv = vUv;
    gl_FragColor = vec4(vPosition.xy, 1.0, 1.0);

    float isOutOfSphere = pow(varyingUv.x - HEAD_CENTER.x, 2.0) + pow(varyingUv.y - HEAD_CENTER.y, 2.0);
    float isOutOfSphereStep = step(pow(HEAD_RADIUS, 2.0), isOutOfSphere);
    float isSphere = 1.0 - isOutOfSphereStep;

    float isStatic = 1.0 - smoothstep(TAIL_LENGTH - 0.3, TAIL_LENGTH, varyingUv.y);
    float xOffset = sin((varyingUv.y + uDistance) * 6.0) / 3.15 * 0.1;
    varyingUv.x += xOffset * isStatic;

    float noiseBlack = snoise(vec3(varyingUv * 3.0, uIndex));
    noiseBlack = smoothstep(0.4, 0.6, noiseBlack * 10.0);
    gl_FragColor = mix(KOI_WHITE, KOI_BLACK, noiseBlack);

    // 0.0, 3.0, 10.0
    float noiseFactor = snoise(vec3(1.0, 1.0, uIndex)) * 10.0;
    float noiseRed = snoise(vec3(varyingUv * noiseFactor, uIndex * 10.0));
    noiseRed = smoothstep(0.4, 0.6, noiseRed * 10.0);
    gl_FragColor = mix(gl_FragColor, KOI_RED, noiseRed);

    float isTail = pointInTriangle(varyingUv, TAIL_1, TAIL_2, TAIL_3);
    isTail = abs(isTail);

    float isPointer = sqrt(pow(vPosition.x - uIntersectPosition.x, 2.0) + pow(vPosition.z - uIntersectPosition.z, 2.0));
    float isPointerStep = 1.0 - smoothstep(LIGHT_RADIUS, LIGHT_RADIUS, isPointer);

    gl_FragColor = mix(WHITE, gl_FragColor, isPointerStep);

    float isAfterHead = step(varyingUv.y, 1.0 - HEAD_RADIUS);
    float isBeforeTail = 1.0 - step(varyingUv.y, TAIL_H - TAIL_A);
    float centeredDistance = abs(varyingUv.x / 2.0 - 0.25);
    // TAIL_H - TAIL_A => TAIL_B / 2.0
    // 1.0 - HEAD_RADIUS => HEAD_RADIUS / 2.0
    float bodyRatio = smoothstep(TAIL_H - TAIL_A, 1.0 - HEAD_RADIUS, varyingUv.y);
    float bodyMin = TAIL_B;
    float bodyMax = HEAD_RADIUS;
    float bodyWidth = bodyMin + (bodyMax - bodyMin) * bodyRatio;
    float isBody = (1.0 - smoothstep(0., bodyWidth / 2.0, centeredDistance)) * isAfterHead * isBeforeTail;
    float isBodyStep = (1.0 - step(bodyWidth / 2.0, centeredDistance)) * isAfterHead * isBeforeTail;

    float newX = map(varyingUv.x, 0.5 - (TAIL_B * (vUv.y / TAIL_LENGTH) / 2.0), 0.5 + (TAIL_B * (vUv.y / TAIL_LENGTH) / 2.0), 0.0, 1.0);
    float isEdgeTail = smoothstep(0.8, 0.95, abs((newX - 0.5)));
    float isEdgeSphere = smoothstep(0.016, 0.019, isOutOfSphere) * isSphere * (1.0 - step(0.1, isTail));
    float isEdge = clamp(isEdgeSphere + isEdgeTail, 0.0, 1.0);

    float isEdgeShadowTail = smoothstep(0.93, 0.98, abs((newX - 0.5)) + 0.02 / vUv.y);
    float isEdgeShadowSphere = smoothstep(0.016, 0.019, isOutOfSphere);
    isEdgeShadowSphere = isEdgeShadowSphere * (1.0 - isBodyStep) * isSphere * (1.0 - step(0.1, isTail));

    float isEdgeShadowBody = 1.0 - smoothstep(0.04, 0.041, isBody);
    isEdgeShadowBody *= isBodyStep;

    float isEdgeShadow = clamp(isEdgeShadowSphere + isEdgeShadowTail + isEdgeShadowBody, 0.0, 1.0);
    gl_FragColor = mix(gl_FragColor, BLACK, isEdgeShadow);
    // gl_FragColor = mix(gl_FragColor, EDGE_COLOR, uIsGlowing * 0.3);

    // gl_FragColor = mix(RED, BLACK, step(0.1, isTail));
    float notTail = 1.0 - step(0.1, isTail);
    // gl_FragColor = mix(gl_FragColor, BLACK, notTail * step(abs(varyingUv.x / 2.0 - 0.25), HEAD_RADIUS / 2.0));

    // gl_FragColor = vec4(0.0, isEdgeShadowBody, 0.0, 1.0);
    if(isTail > 0.0 || isSphere >= 1.0 || isBodyStep >= 1.0) {
    } else {
        discard;
    }

}
